import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { COLORS, CHAT_TOKENS } from '../../../styles/UISystem';
import { IconCircleButton } from '../Icons/IconCircleButton';
import { User } from '@phosphor-icons/react/dist/ssr/User';
import { CaretRight } from '@phosphor-icons/react/dist/ssr/CaretRight';

// Icon pops in when the card lands — the landing-pop icon spring. The
// motion.div has no initial/animate of its own, so it inherits the enter state from
// the card's variant wrapper (contactCardAnimation in ConnectionPageShared). That
// means the same <AnimatePresence initial={false}> gate applies: pops on land, never
// on chat reopen.
const ICON_POP_VARIANTS = {
  hidden: { scale: 0, opacity: 0 },
  visible: { scale: 1, opacity: 1 },
};
const ICON_POP_SPRING = { type: 'spring', damping: 12, stiffness: 200, delay: 0.15 };

/**
 * ContactCardBubble Component
 *
 * iOS-style contact card that appears in chat when Profile shares contacts.
 * Person icon circle + name + right caret. Tappable — navigates to Detail page.
 *
 * Card parity (epic shared rule): both contact cards use the same person icon
 * — never a photo (the Profile's photo is already in the header) — and a right
 * caret to show explicitly that the card links. When contact photos ship, the
 * icon slot upgrades on both cards together.
 *
 * Does NOT show actual contact details (those live on the Detail page).
 *
 * @param {string} name - Profile user's display name
 * @param {boolean} isSent - true for sent (Profile view), false for received (Anon view)
 * @param {function} onClick - Navigation callback (tap → Detail page)
 * @param {string[]} sharedContactTypes - Contact types shared (drives the aboutMe subtitle)
 */
export const ContactCardBubble = ({
  name,
  isSent = false,
  onClick,
  sharedContactTypes = [],
  profileData,
}) => {
  const displayName = name || 'Contact';

  // Subtitle: role + organization if aboutMe is shared
  const hasAboutMe = sharedContactTypes.includes('aboutMe');
  const role = hasAboutMe && profileData?.role ? profileData.role : null;
  const organization = hasAboutMe && profileData?.organization ? profileData.organization : null;
  const subtitle = role && organization ? `${role}, ${organization}` : role || organization || null;

  const bgColor = isSent ? COLORS.CHAT_SENT : COLORS.RECEIVED_GRAY;
  const textColor = isSent ? COLORS.WHITE : COLORS.CHAT_TEXT_DARK;
  // Sent: slightly transparent white circle on blue. Received: white circle on grey.
  const circleColor = isSent ? 'rgba(255,255,255,0.2)' : COLORS.WHITE;
  const iconColor = isSent ? COLORS.WHITE : '#5A5A5A';

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: isSent ? 'flex-end' : 'flex-start',
        marginBottom: '14px',
        width: '100%',
      }}
    >
      <div
        onClick={onClick}
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 12px 10px 10px',
          borderRadius: isSent ? '18px 18px 0 18px' : '18px 18px 18px 0',
          backgroundColor: bgColor,
          cursor: 'pointer',
          maxWidth: '85%',
          boxShadow: CHAT_TOKENS.CARD_SHADOW,
        }}
      >
        <motion.div
          variants={ICON_POP_VARIANTS}
          transition={ICON_POP_SPRING}
          style={{ display: 'flex', flexShrink: 0 }}
        >
          <IconCircleButton
            icon={User}
            inline
            buttonSize={28}
            size={16}
            backgroundColor={circleColor}
            iconColor={iconColor}
          />
        </motion.div>

        {/* Name + subtitle */}
        <div style={{ overflow: 'hidden', flex: 1, minWidth: 0, textAlign: 'left' }}>
          <span
            style={{
              fontSize: '20px',
              fontWeight: '400',
              color: textColor,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: 'block',
            }}
          >
            {displayName}
          </span>
          {subtitle && (
            <span
              style={{
                fontSize: '16px',
                fontWeight: '300',
                marginTop: '-2px',
                color: isSent ? 'rgba(255,255,255,0.6)' : '#999999',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                display: 'block',
              }}
            >
              {subtitle}
            </span>
          )}
        </div>

        {/* Right caret — the card links (parity: both cards carry it) */}
        <CaretRight
          size={16}
          weight="regular"
          color={isSent ? 'rgba(255,255,255,0.7)' : '#C7C7CC'}
          style={{ flexShrink: 0 }}
        />
      </div>
    </div>
  );
};

ContactCardBubble.propTypes = {
  /** Profile user's display name */
  name: PropTypes.string.isRequired,
  /** Whether the message is sent (right-aligned) or received (left-aligned) */
  isSent: PropTypes.bool,
  /** Navigation callback when card is tapped */
  onClick: PropTypes.func,
  /** Contact types shared (e.g., ['phone', 'email']) — drives the aboutMe subtitle */
  sharedContactTypes: PropTypes.arrayOf(PropTypes.string),
};

export default ContactCardBubble;
